import React from 'react';
import styled, { css } from 'styled-components';

import ErrorIcon from '../../assets/badges/error.svg';
import LoadingIcon from '../../assets/badges/loading.svg';
import SuccessIcon from '../../assets/badges/success.svg';
import InfoIcon from '../../assets/info.svg';

export type AlertType = 'error' | 'success' | 'loading' | 'info';

type AlertProps = {
  type: AlertType;
  children: React.ReactNode;
  className?: string;
};

type AlertContainerProps = {
  $type: AlertType;
};

const ALERT_STYLES = {
  error: css`
    background: #ffecec;
    color: #000000 !important;
    border-left: 5px solid #e53935;
  `,
  success: css`
    background: #f4fff7;
    color: #000000 !important;
    border-left: 5px solid #43a047;
  `,
  loading: css`
    background: #fff7d0;
    color: #000000 !important;
    border-left: 5px solid #a9a224;
  `,
  info: css`
    position: relative;
    background: #e9f2ff;
    color: #000000 !important;
    border: none;
    padding-left: 22px;

    &,
    & * {
      color: #000000 !important;
    }

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 10px;
      background: #1e29f6;
      border-radius: 12px 0 0 12px;
    }
  `,
} as const;

const ALERT_ICONS = {
  error: ErrorIcon,
  success: SuccessIcon,
  loading: LoadingIcon,
  info: InfoIcon,
} as const;

const AlertContainer = styled.div<AlertContainerProps>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 18px;
  border-radius: 12px;
  overflow: hidden;
  font-size: 1.6rem;
  width: auto;
  transition: all 0.2s ease-in-out;

  ${({ $type }) => ALERT_STYLES[$type]}

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const IconWrapper = styled.span<{ $type: AlertType }>`
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  flex-shrink: 0;
  color: ${({ $type }) =>
    $type === 'info' ? '#1e29f6 !important' : '#000000'};
`;

const AlertContent = styled.div`
  flex: 1;
  line-height: 1.5;
  font-weight: 500;
  color: #000000 !important;
  white-space: pre-line;
`;

export const Alert: React.FC<AlertProps> = ({ type, children, className }) => {
  const IconComponent = ALERT_ICONS[type];
  const alertRole = type === 'error' ? 'alert' : 'status';
  const alertAriaLive = type === 'error' ? 'assertive' : 'polite';

  return (
    <AlertContainer
      $type={type}
      className={className}
      role={alertRole}
      aria-live={alertAriaLive}
    >
      <IconWrapper $type={type} aria-hidden="true">
        <IconComponent />
      </IconWrapper>
      <AlertContent>{children}</AlertContent>
    </AlertContainer>
  );
};
